import React, { useEffect, useState } from 'react';
import SectionBasePage from 'components/SectionBasePage';
import { useDispatch, useSelector } from 'hooks/store';
import {
  cleanTraysAction,
  createTrayAction,
  traysEntitiesSelector,
  traysIdsSelector,
  getTrayListAction,
  isLoadingTraysSelector,
  remoteTotalSelector,
  updateTrayAction,
} from 'state/slices/tray';
import { usePagination } from 'hooks/pagination';
import {
  Tray,
  PaginationData,
} from 'constants/interface';
import { Table } from 'UI';
import { Form } from 'antd';
import { createAlertNotification } from 'infra/notification';
import { AlertNotificationType, setNotificationAction } from 'state/slices/notification';
import { CreateTrayData } from 'api/tray';
import { unwrapResult } from '@reduxjs/toolkit';

import EditModal, { TrayFormData } from './components/EditModal';
import Extra from './components/Extra';

import {
  ActionButton,
} from './style';

const TrayPage = (): JSX.Element => {
  const dispatch = useDispatch();
  const remoteTotal = useSelector(remoteTotalSelector);

  const trayEntities = useSelector(traysEntitiesSelector);
  const trayIds = useSelector(traysIdsSelector);
  const isRemoteLoading = useSelector(isLoadingTraysSelector);
  const [isLoading, setLoading] = useState(false);

  const trays = trayIds.map((trayId) => {
    const entity = trayEntities[trayId];
    if (!entity) return undefined;
    const tableTray: Tray & {
      key?: string,
    } = {
      ...entity,
      key: trayId.toString(),
    };
    return tableTray;
  }).filter((tableTray): tableTray is Tray => !!tableTray);

  const remoteFetch = (
    paginationData: PaginationData,
  ) => {
    dispatch(getTrayListAction(paginationData));
  };
  const pagination = usePagination<Tray>(
    remoteFetch,
    {
      filter: {
        isActive: true,
      },
    },
  );

  useEffect(() => {
    dispatch(cleanTraysAction());
  }, []);

  useEffect(() => {
    pagination.updateTotal(remoteTotal);
  }, [remoteTotal]);

  const [trayForm] = Form.useForm<TrayFormData>();

  const [isEditModalOpen, setEditModalOpen] = useState(false);
  const [activeEditTray, setActiveEditTray] = useState<Tray>();

  const openModal = (trayId: string) => {
    const trayEntity = trayEntities[trayId];
    setActiveEditTray(trayEntity);
    setEditModalOpen(true);
  };

  const closeModal = () => {
    setActiveEditTray(undefined);
    setEditModalOpen(false);
    trayForm.resetFields();
  };

  const submitModal = async () => {
    try {
      await trayForm.validateFields();
    } catch (e) {
      const errorAlert = createAlertNotification('Verifique os campos com erro.', AlertNotificationType.Error);
      dispatch(setNotificationAction(errorAlert));
      return;
    }

    const formData: TrayFormData = trayForm.getFieldsValue();

    const trayData: CreateTrayData = {
      capacity: parseInt(formData.capacity, 10),
      isHydroponic: formData.isHydroponic,
      resources: formData.resources.map((curResource) => ({
        resourceId: curResource.resourceId,
        resourceConsumption: parseFloat(curResource.resourceConsumption),
      })),
      isActive: true,
    };

    try {
      setLoading(true);
      if (activeEditTray) {
        const updateResult = await dispatch(updateTrayAction({
          id: activeEditTray.id,
          data: {
            ...trayData,
          },
        }));
        unwrapResult(updateResult);
      } else {
        const createResult = await dispatch(createTrayAction(trayData));
        unwrapResult(createResult);
      }
      const successAlert = createAlertNotification(`
      Bandeja ${activeEditTray ? 'atualizada' : 'criada'} com sucesso.
      `, AlertNotificationType.Success);
      dispatch(setNotificationAction(successAlert));
      setLoading(false);
      closeModal();
      pagination.fetch();
    } catch (err) {
      const errorAlert = createAlertNotification('Erro interno ao tentar criar bandeja. Tente novamente.', AlertNotificationType.Error);
      dispatch(setNotificationAction(errorAlert));
      setLoading(false);
    }
  };

  return (
    <SectionBasePage extra={<Extra onAddClick={() => openModal('')} />}>
      <>
        <Table
          dataSource={trays}
          loading={isRemoteLoading}
          showSorterTooltip={false}
          pagination={pagination.getInternal()}
          onChange={pagination.onTableChange}
        >
          <Table.Column
            title="Unidade produtiva"
            key="name"
            dataIndex="id"
            render={(trayId: string) => {
              const tray = trayEntities[trayId];
              const name = `Bandeja${` ${tray?.capacity}` || ''}${tray?.isHydroponic ? ' hidropônica' : ''}`;
              return name;
            }}
          />
          <Table.Column
            title=""
            width="150px"
            dataIndex="id"
            render={(trayId: string) => (
              <ActionButton onClick={() => openModal(trayId)}>
                editar »
              </ActionButton>
            )}
          />
        </Table>
        <EditModal
          formHandler={trayForm}
          onSubmit={submitModal}
          onCancel={closeModal}
          curTray={activeEditTray && {
            capacity: `${activeEditTray.capacity}`,
            isHydroponic: activeEditTray.isHydroponic,
            resources: activeEditTray.resources.map((r) => ({
              resourceId: r.resource.id,
              resourceConsumption: `${r.resourceConsumption}`,
            })),
            id: activeEditTray.id,
          }}
          isLoading={isLoading}
          isVisible={isEditModalOpen}
        />
      </>
    </SectionBasePage>
  );
};

export default TrayPage;
